import * as Constants from "../constants";

/**
 * API client for tenant API keys (integration users).
 *
 *   POST   /apikeys/{name}   -> create an API key/user; returns { returnData: { API_KEY, API_KEY_ID } }
 *   DELETE /apikeys/{id}     -> delete the API key (scoped by the `customer-code` header)
 *
 * Mirrors the legacy html/js/v2/apikey.controller.js. Both calls resolve the
 * full response body (callers inspect `userMessage`/`returnData`).
 *
 * fetch() only rejects on network failure — an HTTP 4xx/5xx still resolves — so
 * `parse` inspects the status and throws a readable Error, and tolerates empty
 * or non-JSON bodies (e.g. a gateway HTML error page) instead of blowing up.
 */

const url = () => `${Constants.baseURL}${Constants.apikeyAPI}`;

async function parse(response) {
  const text = await response.text().catch(() => "");
  let body = {};
  if (text) {
    try {
      body = JSON.parse(text);
    } catch (e) {
      // Body wasn't JSON. On an error status, surface the status; otherwise
      // treat as an empty successful body.
      if (!response.ok) throw new Error(`Request failed (${response.status})`);
      body = {};
    }
  }
  if (!response.ok) {
    const detail =
      body.userMessage || body.message || body.returnData || body.error;
    throw new Error(
      typeof detail === "string" && detail
        ? detail
        : `Request failed (${response.status})`
    );
  }
  return body;
}

/** Create an API key/user. `payload` = { name, email, password }. */
export async function createAPIKey(payload) {
  const headers = Object.assign({}, Constants.commonHeaders);
  const response = await fetch(`${url()}/${payload.name}`, {
    method: "POST",
    headers,
    crossDomain: true,
    body: JSON.stringify(payload),
  });
  return parse(response);
}

/** Delete an API key by id. `customHeaders` carries the `customer-code`. */
export async function deleteAPIKey(apiKeyId, customHeaders = {}) {
  const headers = Object.assign({}, Constants.commonHeaders, customHeaders);
  const response = await fetch(`${url()}/${apiKeyId}`, {
    method: "DELETE",
    headers,
    crossDomain: true,
  });
  return parse(response);
}
